import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';

const formatNum = (n) => { if (!n) return '0'; if (n >= 1e6) return (n/1e6).toFixed(1)+'M'; if (n >= 1e3) return (n/1e3).toFixed(n>=1e4?0:1)+'K'; return String(n); };
const formatPct = (v) => v != null ? (parseFloat(v) * 100).toFixed(1) + '%' : '-';

const tierStyles = { 1: { text:'T1', bg:'bg-amber-500/20', color:'text-amber-400', label:'Tier 1' }, 2: { text:'T2', bg:'bg-zinc-400/20', color:'text-zinc-300', label:'Tier 2' }, 3: { text:'T3', bg:'bg-orange-800/20', color:'text-orange-400', label:'Tier 3' } };
const bssColor = (s) => { if (!s) return 'text-zinc-600'; if (s >= 4) return 'text-green-400'; if (s >= 3) return 'text-yellow-400'; return 'text-red-400'; };

const DemBar = ({ label, value, color }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-zinc-500 w-20">{label}</span>
    <div className="flex-1 h-2 bg-zinc-800 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color || 'bg-nextide-turquoise'}`} style={{ width: `${(parseFloat(value) || 0) * 100}%` }} />
    </div>
    <span className="text-zinc-300 w-12 text-right">{formatPct(value)}</span>
  </div>
);

const platforms = [
  { key: 'twitch', label: 'Twitch', handleField: 'twitch_handle', urlField: 'twitch_url', followField: 'twitch_followers', extra: 'twitch_avg_ccv', extraLabel: 'L30 CCV', color: 'text-purple-400', baseUrl: 'https://twitch.tv/' },
  { key: 'youtube', label: 'YouTube', handleField: 'youtube_handle', urlField: 'youtube_url', followField: 'youtube_followers', color: 'text-red-400', baseUrl: 'https://youtube.com/@' },
  { key: 'twitter', label: 'Twitter/X', handleField: 'twitter_handle', urlField: 'twitter_url', followField: 'twitter_followers', color: 'text-zinc-300', baseUrl: 'https://x.com/' },
  { key: 'instagram', label: 'Instagram', handleField: 'instagram_handle', urlField: 'instagram_url', followField: 'instagram_followers', color: 'text-pink-400', baseUrl: 'https://instagram.com/' },
  { key: 'tiktok', label: 'TikTok', handleField: 'tiktok_handle', urlField: 'tiktok_url', followField: 'tiktok_followers', color: 'text-cyan-400', baseUrl: 'https://tiktok.com/@' },
];

const InputField = ({ label, field, type, placeholder, className, editing, form, setForm, data }) => (
  <div className={`min-w-0 ${className || ''}`}>
    <label className="block text-xs text-zinc-500 mb-1">{label}</label>
    {editing ? (
      <input type={type || 'text'} value={form[field] || ''} onChange={e => setForm(prev => ({ ...prev, [field]: e.target.value }))}
        className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" placeholder={placeholder} />
    ) : (
      <div className="text-white text-sm truncate" title={data[field] || ''}>{data[field] || '-'}</div>
    )}
  </div>
);

const TalentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [talent, setTalent] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newConflict, setNewConflict] = useState({ name: '', type: 'brand' });

  // Campaign history
  const [campaigns, setCampaigns] = useState([]);
  const [showAddCampaign, setShowAddCampaign] = useState(false);
  const [editingCampId, setEditingCampId] = useState(null);
  const emptyCampForm = { campaign_name: '', brand: '', campaign_start: '', campaign_end: '', impressions: '', engagement_rate: '', ctr: '', performance_notes: '', rate_type: '', asset_type: '', creator_rate: '', creator_cost: '' };
  const [campForm, setCampForm] = useState({...emptyCampForm});
  const [managersList, setManagersList] = useState([]);
  const [benchmark, setBenchmark] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [rateComparison, setRateComparison] = useState(null);
  const [enriching, setEnriching] = useState(false);

  useEffect(() => { fetchTalent(); fetchCampaigns(); fetchManagers(); fetchBenchmark(); fetchRevenue(); fetchRateComparison(); }, [id]);

  const fetchRateComparison = async () => {
    try { const res = await api.get(`/talent/${id}/rate-comparison`); setRateComparison(res.data); } catch (e) {}
  };

  const enrichFromInsightIQ = async () => {
    setEnriching(true);
    try {
      const res = await api.post(`/talent/${id}/enrich`);
      if (res.data.success) {
        alert(`Enriched! Updated ${res.data.fieldsUpdated} fields from InsightIQ.`);
        fetchTalent();
      } else {
        alert(res.data.error || 'No data returned');
      }
    } catch (e) {
      alert(e.response?.data?.error || 'Enrichment failed — check Instagram handle');
    } finally { setEnriching(false); }
  };

  const fetchRevenue = async () => {
    try { const res = await api.get(`/talent/${id}/revenue`); setRevenueData(res.data); } catch (e) {}
  };

  const fetchBenchmark = async () => {
    try { const res = await api.get(`/talent/${id}/benchmark`); setBenchmark(res.data); } catch (e) {}
  };

  const fetchManagers = async () => {
    try { const res = await api.get('/talent-managers'); setManagersList(res.data.managers || []); } catch (e) {}
  };

  const fetchTalent = async () => {
    try {
      const res = await api.get(`/talent/${id}`);
      setTalent(res.data.talent);
      setForm({ ...res.data.talent, conflicts: res.data.talent.conflicts || [] });
    } catch (e) { navigate('/talent'); }
    finally { setLoading(false); }
  };

  const fetchCampaigns = async () => {
    try { const res = await api.get(`/talent/${id}/campaigns`); setCampaigns(res.data.campaigns || []); } catch (e) {}
  };

  const addCampaign = async () => {
    if (!campForm.campaign_name) return;
    try {
      await api.post(`/talent/${id}/campaigns`, campForm);
      setCampForm({...emptyCampForm});
      setShowAddCampaign(false);
      fetchCampaigns();
    } catch (e) { alert('Failed to add campaign'); }
  };

  const saveCampaign = async () => {
    if (!editingCampId || !campForm.campaign_name) return;
    try {
      await api.put(`/talent/campaigns/${editingCampId}`, campForm);
      setCampForm({...emptyCampForm});
      setEditingCampId(null);
      fetchCampaigns();
    } catch (e) { alert('Failed to save campaign'); }
  };

  const startEditCampaign = (c) => {
    setEditingCampId(c.id);
    setShowAddCampaign(false);
    setCampForm({
      campaign_name: c.campaign_name || '', brand: c.brand || '',
      campaign_start: c.campaign_start?.split('T')[0] || '', campaign_end: c.campaign_end?.split('T')[0] || '',
      impressions: c.impressions || '', engagement_rate: c.engagement_rate || '', ctr: c.ctr || '',
      performance_notes: c.performance_notes || '',
      rate_type: c.rate_type || '', asset_type: c.asset_type || '',
      creator_rate: c.creator_rate || '', creator_cost: c.creator_cost || ''
    });
  };

  const deleteCampaign = async (campId) => {
    try { await api.delete(`/talent/campaigns/${campId}`); fetchCampaigns(); } catch (e) {}
  };

  const save = async () => {
    setSaving(true);
    try { await api.put(`/talent/${id}`, form); setEditing(false); fetchTalent(); }
    catch (e) { alert('Failed to save'); } finally { setSaving(false); }
  };

  const deleteTalent = async () => {
    if (!window.confirm('Delete this talent?')) return;
    try { await api.delete(`/talent/${id}`); navigate('/talent'); } catch (e) { alert('Failed'); }
  };

  const addConflict = () => {
    if (!newConflict.name) return;
    setForm(prev => ({ ...prev, conflicts: [...(prev.conflicts || []), { ...newConflict }] }));
    setNewConflict({ name: '', type: 'brand' });
  };

  const removeConflict = (idx) => {
    setForm(prev => ({ ...prev, conflicts: prev.conflicts.filter((_, i) => i !== idx) }));
  };

  const f = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  if (loading) return <div className="p-8 text-zinc-400">Loading...</div>;
  if (!talent) return null;

  const d = editing ? form : talent;
  const ti = tierStyles[d.talent_tier] || { text: '--', bg: 'bg-zinc-800', color: 'text-zinc-600', label: 'Untiered' };
  const conflicts = (editing ? form.conflicts : talent.conflicts) || [];
  const isManaged = d.manager_name && d.manager_name.toLowerCase() !== 'self';
  const inputProps = { editing, form, setForm, data: d };

  return (
    <div className="p-8 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button onClick={() => navigate(-1)} className="text-zinc-400 hover:text-white text-sm mb-2 block">← Back</button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-white">{d.name}</h1>
            <span className={`px-2 py-0.5 rounded text-xs font-semibold ${ti.bg} ${ti.color}`}>{ti.label}</span>
            <span className={`text-sm font-semibold ${bssColor(d.brand_safety_score)}`}>
              BSS {d.brand_safety_score ? parseFloat(d.brand_safety_score).toFixed(1) : '-'}
            </span>

          </div>
        </div>
        <div className="flex gap-3">
          {!editing ? (
            <>
              <button onClick={() => setEditing(true)} className="bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm">Edit</button>
              <button onClick={deleteTalent} className="bg-red-500/20 text-red-400 px-4 py-2 rounded-lg text-sm">Delete</button>
            </>
          ) : (
            <>
              <button onClick={() => { setEditing(false); setForm({ ...talent, conflicts: talent.conflicts || [] }); }} className="bg-zinc-800 text-white px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={save} disabled={saving} className="bg-nextide-turquoise text-black px-4 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">{saving ? 'Saving...' : 'Save'}</button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Left column - 2/3 */}
        <div className="col-span-2 space-y-6">
          {/* Identity & Representation */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Identity & Representation</h3>
            <div className="grid grid-cols-3 gap-4">
              <InputField {...inputProps} label="Name" field="name" />
              <InputField {...inputProps} label="Gender" field="gender" />
              <InputField {...inputProps} label="Race" field="race" />
              <InputField {...inputProps} label="Primary Category" field="primary_category" />
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Talent Tier</label>
                {editing ? (
                  <select value={form.talent_tier || ''} onChange={e => f('talent_tier', e.target.value ? parseInt(e.target.value) : null)}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">Untiered</option><option value="1">Tier 1</option><option value="2">Tier 2</option><option value="3">Tier 3</option>
                  </select>
                ) : <div className="text-white text-sm">{ti.label}</div>}
              </div>

            </div>
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-zinc-800">
              {editing ? (
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Manager</label>
                  <select value={form.manager_name || ''} onChange={e => {
                    const name = e.target.value;
                    if (name === '__new__') {
                      const newName = prompt('New manager name:');
                      if (newName) {
                        const newEmail = prompt('Manager email:');
                        const newAgency = prompt('Agency:');
                        setForm(prev => ({...prev, manager_name: newName, manager_email: newEmail || '', agency: newAgency || prev.agency || ''}));
                      }
                    } else if (name === 'Self') {
                      setForm(prev => ({...prev, manager_name: 'Self', manager_email: ''}));
                    } else {
                      const mgr = managersList.find(m => m.name === name);
                      setForm(prev => ({...prev, manager_name: name, manager_email: mgr?.email || prev.manager_email || '', agency: mgr?.agency || prev.agency || ''}));
                    }
                  }} className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm">
                    <option value="">Select...</option>
                    <option value="Self">Self (not repped)</option>
                    {managersList.map(m => <option key={m.name} value={m.name}>{m.name}{m.agency ? ` (${m.agency})` : ''}{m.email ? ` — ${m.email}` : ''}</option>)}
                    {form.manager_name && !managersList.find(m => m.name === form.manager_name) && form.manager_name !== 'Self' && (
                      <option value={form.manager_name}>{form.manager_name}</option>
                    )}
                    <option value="__new__">+ Add new manager...</option>
                  </select>
                </div>
              ) : (
                <InputField {...inputProps} label="Manager" field="manager_name" />
              )}
              {editing && form.manager_name && form.manager_name !== 'Self' && form.manager_name !== '' && (
                <div className="flex gap-2 mt-1">
                  <button type="button" onClick={async () => {
                    const newName = prompt(`Rename "${form.manager_name}" to:`, form.manager_name);
                    if (!newName || newName === form.manager_name) return;
                    const newEmail = prompt('Updated email:', form.manager_email || '');
                    const newAgency = prompt('Updated agency:', form.agency || '');
                    try {
                      await api.put('/talent-managers/rename', { old_name: form.manager_name, new_name: newName, new_email: newEmail || '', new_agency: newAgency || '' });
                      setForm(prev => ({...prev, manager_name: newName, manager_email: newEmail || '', agency: newAgency || prev.agency}));
                      fetchManagers();
                      alert(`Updated "${form.manager_name}" → "${newName}" across all talent`);
                    } catch (e) { alert('Failed'); }
                  }} className="text-zinc-500 hover:text-nextide-turquoise text-xs">Edit manager globally</button>
                  <button type="button" onClick={async () => {
                    if (!window.confirm(`Remove "${form.manager_name}" from ALL talent? They'll be set to "Self".`)) return;
                    try {
                      await api.delete(`/talent-managers/${encodeURIComponent(form.manager_name)}`);
                      setForm(prev => ({...prev, manager_name: 'Self', manager_email: ''}));
                      fetchManagers();
                      alert('Manager removed from all talent');
                    } catch (e) { alert('Failed'); }
                  }} className="text-zinc-500 hover:text-red-400 text-xs">Delete manager</button>
                </div>
              )}
              {(editing || isManaged) && <InputField {...inputProps} label="Manager Email" field="manager_email" type="email" />}
              {(editing || !isManaged) && <InputField {...inputProps} label="Contact Email" field="contact_email" type="email" />}
              <InputField {...inputProps} label="Agency" field="agency" />
            </div>
          </div>

          {/* Audience Demographics */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide">Audience Demographics</h3>
              {(d.instagram_handle || d.instagram_url) && (
                <button onClick={enrichFromInsightIQ} disabled={enriching}
                  className="text-xs px-2.5 py-1 rounded bg-purple-600/20 text-purple-400 hover:bg-purple-600/30 disabled:opacity-50 transition-colors">
                  {enriching ? 'Enriching...' : '✦ Pull from InsightIQ'}
                </button>
              )}
            </div>
            {editing ? (
              <div className="grid grid-cols-3 gap-3">
                {[['aud_male','Male %'],['aud_female','Female %'],['aud_u18','U18 %'],
                  ['aud_18_24','18-24 %'],['aud_25_34','25-34 %'],['aud_35_44','35-44 %'],['aud_45_54','45-54 %'],['aud_55_plus','55+ %'],
                  ['aud_usa','USA %'],['aud_canada','Canada %'],['aud_uk','UK %'],['aud_australia','Australia %'],
                  ['aud_black','Black/AA %'],['aud_white','White %'],['aud_asian','Asian %'],['aud_hispanic','Hispanic %']].map(([k,l]) => (
                  <div key={k}>
                    <label className="block text-xs text-zinc-500 mb-1">{l}</label>
                    <input type="number" step="0.001" min="0" max="1" value={form[k] || ''} onChange={e => f(k, e.target.value)}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-2 text-white text-sm" placeholder="0.000" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-zinc-400 text-xs font-semibold mb-1">Gender</div>
                <DemBar label="Male" value={d.aud_male} color="bg-blue-400" />
                <DemBar label="Female" value={d.aud_female} color="bg-pink-400" />
                <div className="text-zinc-400 text-xs font-semibold mt-3 mb-1">Age</div>
                <DemBar label="Under 18" value={d.aud_u18} color="bg-orange-400" />
                <DemBar label="18-34" value={(parseFloat(d.aud_18_24 || 0) + parseFloat(d.aud_25_34 || 0)) || d.aud_18_34} color="bg-nextide-turquoise" />
                <DemBar label="35+" value={(parseFloat(d.aud_35_44 || 0) + parseFloat(d.aud_45_54 || 0) + parseFloat(d.aud_55_plus || 0)) || d.aud_35_plus} color="bg-violet-400" />
                <div className="text-zinc-400 text-xs font-semibold mt-3 mb-1">Geography</div>
                <DemBar label="USA" value={d.aud_usa} color="bg-blue-500" />
                <DemBar label="Canada" value={d.aud_canada} color="bg-red-500" />
                <DemBar label="UK" value={d.aud_uk} color="bg-indigo-400" />
                <DemBar label="Australia" value={d.aud_australia} color="bg-yellow-500" />
                <div className="text-zinc-400 text-xs font-semibold mt-3 mb-1">Race</div>
                <DemBar label="Black/AA" value={d.aud_black} />
                <DemBar label="White" value={d.aud_white} />
                <DemBar label="Asian" value={d.aud_asian} />
                <DemBar label="Hispanic" value={d.aud_hispanic} />
              </div>
            )}
          </div>

          {/* Audience Interests */}
          {d.aud_interests && d.aud_interests.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Audience Interests</h3>
              <div className="space-y-2">
                {d.aud_interests.map((interest, i) => {
                  const pct = parseFloat(interest.value) || 0;
                  const pctDisplay = (pct * 100).toFixed(1) + '%';
                  const barWidth = Math.min(pct * 100, 100);
                  return (
                    <div key={i} className="flex items-center gap-3">
                      <div className="w-32 text-xs text-zinc-300 truncate shrink-0">{interest.name}</div>
                      <div className="flex-1 bg-zinc-800 rounded-full h-2 relative">
                        <div className="bg-nextide-turquoise h-2 rounded-full" style={{ width: barWidth + '%' }} />
                      </div>
                      <div className="w-10 text-right text-xs text-zinc-400 shrink-0">{pctDisplay}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Platform Presence */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Platform Presence</h3>
            <div className="space-y-3">
              {platforms.map(p => {
                const handle = d[p.handleField];
                const url = d[p.urlField] || (handle ? p.baseUrl + handle : '');
                const followers = d[p.followField];
                return (
                  <div key={p.key} className="flex items-center gap-4 py-2 border-b border-zinc-800/50 last:border-0">
                    <span className={`w-20 text-sm font-medium ${p.color}`}>{p.label}</span>
                    {editing ? (
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <input type="text" value={form[p.handleField] || ''} onChange={e => f(p.handleField, e.target.value)}
                          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs" placeholder="Handle" />
                        <input type="text" value={form[p.urlField] || ''} onChange={e => {
                          const url = e.target.value;
                          f(p.urlField, url);
                          const extractHandle = (url, key) => {
                            try {
                              const clean = url.trim().replace(/\/$/, '');
                              // YouTube /channel/UCxxxx URLs are internal IDs — can't resolve to handle without API
                              if (key === 'youtube' && /youtube\.com\/channel\/UC[a-zA-Z0-9_-]+/i.test(clean)) return null;
                              const patterns = {
                                twitch:    /twitch\.tv\/([^/?#]+)/i,
                                youtube:   /youtube\.com\/(?:@|c\/|user\/)([^/?#]+)/i,
                                instagram: /instagram\.com\/([^/?#]+)/i,
                                tiktok:    /tiktok\.com\/@?([^/?#]+)/i,
                                twitter:   /(?:twitter|x)\.com\/([^/?#]+)/i,
                              };
                              const match = clean.match(patterns[key]);
                              return match ? match[1].replace(/^@/, '') : null;
                            } catch { return null; }
                          };
                          const handle = extractHandle(url, p.key);
                          if (handle) f(p.handleField, handle);
                        }}
                          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs" placeholder="Full URL" />
                        <input type="number" value={form[p.followField] || ''} onChange={e => f(p.followField, parseInt(e.target.value) || 0)}
                          className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs" placeholder="Followers" />
                        {p.extra && (
                          <div className="col-span-3 flex items-center gap-2">
                            <input type="number" value={form[p.extra] || ''} onChange={e => f(p.extra, parseInt(e.target.value) || 0)}
                              className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs flex-1" placeholder={p.extraLabel} />
                            {p.key === 'twitch' && form[p.handleField] && (
                              <a
                                href={`https://twitchtracker.com/${form[p.handleField]}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-zinc-500 hover:text-purple-400 text-xs whitespace-nowrap transition-colors border border-zinc-700 rounded px-2 py-1.5"
                                title="Open TwitchTracker to look up L30 CCV"
                              >↗ TwitchTracker</a>
                            )}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <span className="text-zinc-400 text-sm w-28">{handle || '-'}</span>
                        {url && handle ? (
                          <a href={url} target="_blank" rel="noopener noreferrer" className="text-nextide-turquoise text-xs hover:underline">Link</a>
                        ) : <span className="text-zinc-700 text-xs w-8">-</span>}
                        <span className="text-white text-sm font-medium ml-auto">{followers > 0 ? formatNum(followers) : '-'}</span>
                        {p.extra && d[p.extra] > 0 && (
                          <span className="text-purple-400 text-xs ml-2">{p.extraLabel}: {formatNum(d[p.extra])}</span>
                        )}
                        {p.key === 'twitch' && handle && (
                          <a
                            href={`https://twitchtracker.com/${handle}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-zinc-500 hover:text-purple-400 text-xs ml-2 transition-colors"
                            title="Open TwitchTracker to update L30 CCV"
                          >↗ TT</a>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
              <div className="flex items-center justify-between pt-2 border-t border-zinc-700">
                <span className="text-white text-sm font-semibold">Total Following</span>
                <span className="text-nextide-turquoise text-sm font-bold">{formatNum(d.total_following)}</span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Notes</h3>
            {editing ? (
              <textarea value={form.notes || ''} onChange={e => f('notes', e.target.value)} rows={4}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm" />
            ) : (
              <div className="text-zinc-300 text-sm whitespace-pre-wrap">{d.notes || 'No notes'}</div>
            )}
          </div>

          {/* Revenue Attribution */}
          {revenueData?.deals?.length > 0 && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Revenue Attribution</h3>
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-zinc-500 text-xs mb-1">Won Revenue</div>
                  <div className="text-green-400 text-xl font-bold">${formatNum(Math.round(revenueData.summary.attributed_won))}</div>
                  <div className="text-zinc-600 text-xs">{revenueData.summary.won_deals} deal{revenueData.summary.won_deals !== 1 ? 's' : ''}</div>
                </div>
                <div className="text-center">
                  <div className="text-zinc-500 text-xs mb-1">Pipeline</div>
                  <div className="text-yellow-400 text-xl font-bold">${formatNum(Math.round(revenueData.summary.attributed_pipeline))}</div>
                  <div className="text-zinc-600 text-xs">{revenueData.summary.pipeline_deals} deal{revenueData.summary.pipeline_deals !== 1 ? 's' : ''}</div>
                </div>
                <div className="text-center">
                  <div className="text-zinc-500 text-xs mb-1">Avg Deal Size</div>
                  <div className="text-white text-xl font-bold">${formatNum(Math.round(revenueData.summary.avg_deal_size))}</div>
                  <div className="text-zinc-600 text-xs">{revenueData.summary.total_deals} total</div>
                </div>
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {revenueData.deals.map(d => (
                  <div key={d.id} className="flex items-center justify-between bg-zinc-800 rounded-lg px-3 py-2">
                    <div>
                      <div className="text-white text-sm font-medium">{d.deal_name}</div>
                      <div className="text-zinc-500 text-xs">{d.account} | {d.creators_on_deal} creators on deal</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-semibold ${d.stage === 'closed_won' ? 'text-green-400' : d.stage === 'closed_lost' ? 'text-red-400' : 'text-yellow-400'}`}>
                        ${formatNum(Math.round(parseFloat(d.attributed_share)))}
                      </div>
                      <div className="text-zinc-600 text-xs">${formatNum(Math.round(parseFloat(d.total_deal_value)))} total</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Performance vs Benchmark */}
          {benchmark?.hasBenchmark && benchmark.tierBenchmark && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Performance vs T{benchmark.tierBenchmark.tier} Average</h3>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { label: 'Avg Impressions', creator: benchmark.creator.avg_impressions, tier: benchmark.tierBenchmark.avg_impressions, format: v => formatNum(Math.round(v)) },
                  { label: 'Avg Eng. Rate', creator: benchmark.creator.avg_er, tier: benchmark.tierBenchmark.avg_er, format: v => v ? v.toFixed(2) + '%' : '-' },
                  { label: 'Avg CTR', creator: benchmark.creator.avg_ctr, tier: benchmark.tierBenchmark.avg_ctr, format: v => v ? v.toFixed(2) + '%' : '-' },
                ].map(m => {
                  const mult = m.tier > 0 ? m.creator / m.tier : 0;
                  const isAbove = mult >= 1;
                  return (
                    <div key={m.label} className="text-center">
                      <div className="text-zinc-500 text-xs mb-1">{m.label}</div>
                      <div className="text-white text-lg font-bold">{m.format(m.creator)}</div>
                      <div className={`text-xs font-semibold mt-1 ${isAbove ? 'text-green-400' : 'text-red-400'}`}>
                        {mult > 0 ? `${mult.toFixed(1)}x` : '-'} <span className="text-zinc-600 font-normal">vs T{benchmark.tierBenchmark.tier} avg ({m.format(m.tier)})</span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="text-zinc-600 text-xs text-center mt-3">Based on {benchmark.creator.campaign_count} campaign{benchmark.creator.campaign_count !== 1 ? 's' : ''} vs {benchmark.tierBenchmark.campaign_count} T{benchmark.tierBenchmark.tier} campaigns</div>
            </div>
          )}

          {/* Campaign History */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide">Campaign History</h3>
              <button onClick={() => { setShowAddCampaign(!showAddCampaign); setEditingCampId(null); setCampForm({...emptyCampForm}); }}
                className="text-nextide-turquoise text-xs hover:underline">{showAddCampaign ? 'Cancel' : '+ Add Campaign'}</button>
            </div>

            {(showAddCampaign || editingCampId) && (
              <div className="bg-zinc-800 rounded-lg p-4 mb-4 space-y-3">
                <div className="text-white text-sm font-medium mb-2">{editingCampId ? 'Edit Campaign' : 'New Campaign'}</div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs text-zinc-500 mb-1">Campaign Name *</label>
                    <input type="text" value={campForm.campaign_name} onChange={e => setCampForm({...campForm, campaign_name: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                  <div><label className="block text-xs text-zinc-500 mb-1">Brand</label>
                    <input type="text" value={campForm.brand} onChange={e => setCampForm({...campForm, brand: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="block text-xs text-zinc-500 mb-1">Start Date</label>
                    <input type="date" value={campForm.campaign_start} onChange={e => setCampForm({...campForm, campaign_start: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                  <div><label className="block text-xs text-zinc-500 mb-1">End Date</label>
                    <input type="date" value={campForm.campaign_end} onChange={e => setCampForm({...campForm, campaign_end: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div><label className="block text-xs text-zinc-500 mb-1">Impressions</label>
                    <input type="number" value={campForm.impressions} onChange={e => setCampForm({...campForm, impressions: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                  <div><label className="block text-xs text-zinc-500 mb-1">Eng. Rate %</label>
                    <input type="number" step="0.0001" value={campForm.engagement_rate} onChange={e => setCampForm({...campForm, engagement_rate: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" placeholder="0.32" /></div>
                  <div><label className="block text-xs text-zinc-500 mb-1">CTR %</label>
                    <input type="number" step="0.0001" value={campForm.ctr} onChange={e => setCampForm({...campForm, ctr: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" placeholder="1.23" /></div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                  <div><label className="block text-xs text-zinc-500 mb-1">Rate Type</label>
                    <select value={campForm.rate_type} onChange={e => setCampForm({...campForm, rate_type: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm">
                      <option value="">Select...</option><option value="CPM">CPM</option><option value="FLAT">Flat</option>
                    </select></div>
                  <div><label className="block text-xs text-zinc-500 mb-1">Asset Type</label>
                    <select value={campForm.asset_type} onChange={e => setCampForm({...campForm, asset_type: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm">
                      <option value="">Select...</option>
                      <option value="OmniBanner">OmniBanner</option><option value="Immersive Frames">Immersive Frames</option>
                      <option value="Sponsored Stream">Sponsored Stream</option><option value="Custom">Custom</option>
                    </select></div>
                  <div><label className="block text-xs text-zinc-500 mb-1">Rate ($)</label>
                    <input type="number" step="0.01" value={campForm.creator_rate} onChange={e => setCampForm({...campForm, creator_rate: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                  <div><label className="block text-xs text-zinc-500 mb-1">Total Cost ($)</label>
                    <input type="number" step="0.01" value={campForm.creator_cost} onChange={e => setCampForm({...campForm, creator_cost: e.target.value})}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                </div>
                <div><label className="block text-xs text-zinc-500 mb-1">Notes</label>
                  <input type="text" value={campForm.performance_notes} onChange={e => setCampForm({...campForm, performance_notes: e.target.value})}
                    className="w-full bg-zinc-700 border border-zinc-600 rounded px-3 py-1.5 text-white text-sm" /></div>
                <div className="flex gap-2">
                  {editingCampId ? (
                    <>
                      <button onClick={saveCampaign} disabled={!campForm.campaign_name}
                        className="bg-nextide-turquoise text-black px-4 py-2 rounded text-sm font-semibold disabled:opacity-50">Save</button>
                      <button onClick={() => { setEditingCampId(null); setCampForm({...emptyCampForm}); }}
                        className="bg-zinc-700 text-white px-4 py-2 rounded text-sm">Cancel</button>
                    </>
                  ) : (
                    <button onClick={addCampaign} disabled={!campForm.campaign_name}
                      className="bg-nextide-turquoise text-black px-4 py-2 rounded text-sm font-semibold disabled:opacity-50">Add Campaign</button>
                  )}
                </div>
              </div>
            )}

            {campaigns.length === 0 && !showAddCampaign && !editingCampId ? (
              <div className="text-zinc-600 text-sm">No campaigns recorded</div>
            ) : (
              <div className="space-y-3">
                {campaigns.map(c => (
                  <div key={c.id} className="bg-zinc-800 rounded-lg px-4 py-3">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="text-white text-sm font-medium">{c.campaign_name}</div>
                        <div className="text-zinc-500 text-xs">{c.brand}{c.campaign_start ? ` | ${new Date(c.campaign_start).toLocaleDateString()}` : ''}{c.campaign_end ? ` - ${new Date(c.campaign_end).toLocaleDateString()}` : ''}</div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => startEditCampaign(c)} className="text-zinc-500 hover:text-nextide-turquoise text-xs">edit</button>
                        <button onClick={() => deleteCampaign(c.id)} className="text-zinc-600 hover:text-red-400 text-xs">x</button>
                      </div>
                    </div>
                    {(c.impressions > 0 || c.engagement_rate || c.ctr) && (
                      <div className="flex gap-4 mt-2 text-xs">
                        {c.impressions > 0 && <span className="text-nextide-turquoise">{parseInt(c.impressions).toLocaleString()} impr.</span>}
                        {c.engagement_rate && <span className="text-zinc-300">{parseFloat(c.engagement_rate).toFixed(2)}% eng.</span>}
                        {c.ctr && <span className="text-zinc-300">{parseFloat(c.ctr).toFixed(2)}% CTR</span>}
                      </div>
                    )}
                    {(c.rate_type || c.asset_type || c.creator_rate || c.creator_cost) && (
                      <div className="flex gap-4 mt-1 text-xs">
                        {c.asset_type && <span className="text-zinc-400">{c.asset_type}</span>}
                        {c.rate_type && c.creator_rate && <span className="text-zinc-300">{c.rate_type}: ${parseFloat(c.creator_rate).toLocaleString()}</span>}
                        {c.creator_cost && <span className="text-yellow-400">Cost: ${parseFloat(c.creator_cost).toLocaleString()}</span>}
                      </div>
                    )}
                    {c.performance_notes && <div className="text-zinc-500 text-xs mt-1">{c.performance_notes}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column - 1/3 */}
        <div className="space-y-6">
          {/* Brand Safety */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Brand Safety</h3>
            <div className={`text-3xl font-bold mb-4 ${bssColor(d.brand_safety_score)}`}>
              {d.brand_safety_score ? parseFloat(d.brand_safety_score).toFixed(1) : '-'}
            </div>
            {editing ? (
              <div className="grid grid-cols-2 gap-2">
                {[['bss_content_quality','Content Quality'],['bss_language','Language'],['bss_topics','Topics'],
                  ['bss_audience','Audience'],['bss_engagement','Engagement'],['bss_history','History'],['bss_compliance','Compliance']].map(([k,l]) => (
                  <div key={k}>
                    <label className="text-xs text-zinc-500">{l}</label>
                    <input type="number" step="0.1" min="0" max="5" value={form[k] || ''} onChange={e => {
                      const updated = { ...form, [k]: e.target.value };
                      // Auto-compute BSS average
                      const scores = ['bss_content_quality','bss_language','bss_topics','bss_audience','bss_engagement','bss_history','bss_compliance']
                        .map(s => parseFloat(updated[s])).filter(n => !isNaN(n));
                      if (scores.length > 0) updated.brand_safety_score = (scores.reduce((a,b) => a+b, 0) / scores.length).toFixed(1);
                      setForm(updated);
                    }} className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {[['bss_content_quality','Content Quality'],['bss_language','Language'],['bss_topics','Topics'],
                  ['bss_audience','Audience'],['bss_engagement','Engagement'],['bss_history','History'],['bss_compliance','Compliance']].map(([k,l]) => (
                  <div key={k} className="flex justify-between text-xs">
                    <span className="text-zinc-500">{l}</span>
                    <span className={bssColor(d[k])}>{d[k] ? parseFloat(d[k]).toFixed(1) : '-'}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Rates (internal) */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide">Rates</h3>
              <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded">INTERNAL</span>
            </div>
            <div className="space-y-3">
              {[['rate_omnibanner_cpm','OmniBanner CPM','$'],['rate_immersive_frame_cpm','Immersive Frame CPM','$']].map(([k,l,pre]) => (
                <div key={k}>
                  <label className="block text-xs text-zinc-500 mb-1">{l}</label>
                  {editing ? (
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 text-xs">{pre}</span>
                      <input type="number" step="0.01" value={form[k] || ''} onChange={e => f(k, e.target.value)}
                        className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 pl-5 text-white text-xs" />
                    </div>
                  ) : (
                    <div className="text-white text-sm">{d[k] ? `$${parseFloat(d[k]).toFixed(2)}` : '-'}</div>
                  )}
                </div>
              ))}
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Sponsored Stream Est. (per hr)</label>
                <div className="text-white text-sm">
                  {d.twitch_avg_ccv && parseInt(d.twitch_avg_ccv) > 0
                    ? `$${(parseInt(d.twitch_avg_ccv) * 1.5).toLocaleString()}`
                    : '-'}
                  {d.twitch_avg_ccv && parseInt(d.twitch_avg_ccv) > 0 && (
                    <span className="text-zinc-500 text-xs ml-2">{parseInt(d.twitch_avg_ccv).toLocaleString()} CCV × $1.50</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Rate vs Market */}
          {rateComparison?.hasComparison && (rateComparison.creatorRates?.length > 0 || rateComparison.talent?.rate_omnibanner_cpm || rateComparison.talent?.rate_immersive_frame_cpm) && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide">Rate vs Market</h3>
                <span className="text-[10px] px-1.5 py-0.5 bg-zinc-800 text-zinc-500 rounded">T{rateComparison.talent.tier} AVG</span>
              </div>
              <div className="space-y-3">
                {/* Static rate card comparisons */}
                {[
                  { label: 'OmniBanner CPM', creatorVal: rateComparison.talent.rate_omnibanner_cpm, asset: 'OmniBanner', rType: 'CPM' },
                  { label: 'Immersive Frame CPM', creatorVal: rateComparison.talent.rate_immersive_frame_cpm, asset: 'Immersive Frames', rType: 'CPM' },
                ].map(item => {
                  const tierAvg = rateComparison.tierRates?.find(r => r.asset_type === item.asset && r.rate_type === item.rType);
                  if (!item.creatorVal && !tierAvg) return null;
                  const tierRate = tierAvg ? parseFloat(tierAvg.avg_rate) : null;
                  const diff = item.creatorVal && tierRate ? ((item.creatorVal - tierRate) / tierRate * 100).toFixed(0) : null;
                  return (
                    <div key={item.label} className="flex items-center justify-between">
                      <div>
                        <div className="text-zinc-400 text-xs">{item.label}</div>
                        <div className="text-white text-sm font-medium">{item.creatorVal ? `$${item.creatorVal.toFixed(2)}` : 'Not set'}</div>
                      </div>
                      {tierRate && (
                        <div className="text-right">
                          <div className="text-zinc-500 text-xs">T{rateComparison.talent.tier} avg: ${tierRate.toFixed(2)}</div>
                          {diff && <div className={`text-xs font-semibold ${parseFloat(diff) <= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {parseFloat(diff) <= 0 ? `${Math.abs(diff)}% below` : `${diff}% above`}
                          </div>}
                        </div>
                      )}
                    </div>
                  );
                }).filter(Boolean)}
                {/* Sponsored stream estimate */}
                {rateComparison.talent.ccv > 0 && (
                  <div className="flex items-center justify-between pt-2 border-t border-zinc-800">
                    <div>
                      <div className="text-zinc-400 text-xs">Sponsored Stream (per hr)</div>
                      <div className="text-white text-sm font-medium">${(rateComparison.talent.ccv * 1.5).toLocaleString()}</div>
                    </div>
                    <div className="text-zinc-500 text-xs text-right">{formatNum(rateComparison.talent.ccv)} CCV × $1.50</div>
                  </div>
                )}
                {/* Historical rate data */}
                {rateComparison.creatorRates?.length > 0 && (
                  <div className="pt-2 border-t border-zinc-800">
                    <div className="text-zinc-500 text-xs mb-2">Historical Rates</div>
                    {rateComparison.creatorRates.map((r, i) => {
                      const tierAvg = rateComparison.tierRates?.find(tr => tr.asset_type === r.asset_type && tr.rate_type === r.rate_type);
                      return (
                        <div key={i} className="flex items-center justify-between py-1">
                          <span className="text-zinc-400 text-xs">{r.asset_type} <span className={`px-1 py-0.5 rounded ${r.rate_type === 'CPM' ? 'bg-blue-500/20 text-blue-400' : 'bg-yellow-500/20 text-yellow-400'}`}>{r.rate_type}</span></span>
                          <span className="text-white text-xs">${parseFloat(r.avg_rate).toFixed(2)} avg <span className="text-zinc-600">({r.count}x)</span>
                            {tierAvg && <span className="text-zinc-500 ml-1">vs ${parseFloat(tierAvg.avg_rate).toFixed(2)}</span>}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Conflicts */}
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
            <h3 className="text-sm font-semibold text-zinc-400 uppercase tracking-wide mb-4">Conflicts</h3>
            {conflicts.length === 0 && !editing && <div className="text-zinc-600 text-sm">No conflicts</div>}
            <div className="space-y-2">
              {conflicts.map((c, i) => (
                <div key={i} className="flex items-center justify-between bg-zinc-800 rounded-lg px-3 py-2">
                  <div>
                    <span className="text-white text-sm">{c.name}</span>
                    <span className="text-zinc-500 text-xs ml-2 capitalize">{c.type === 'brand' ? 'Brand Partner' : 'Blocked Category'}</span>
                  </div>
                  {editing && (
                    <button onClick={() => removeConflict(i)} className="text-zinc-500 hover:text-red-400 text-xs">Remove</button>
                  )}
                </div>
              ))}
            </div>
            {editing && (
              <div className="mt-3 flex gap-2">
                <input type="text" value={newConflict.name} onChange={e => setNewConflict({...newConflict, name: e.target.value})}
                  placeholder="Brand or category" className="flex-1 bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs" />
                <select value={newConflict.type} onChange={e => setNewConflict({...newConflict, type: e.target.value})}
                  className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-xs">
                  <option value="brand">Brand</option><option value="category">Category</option>
                </select>
                <button onClick={addConflict} disabled={!newConflict.name}
                  className="bg-zinc-700 text-white px-3 py-1.5 rounded text-xs disabled:opacity-50">Add</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TalentDetail;
